/**
 * Pagina /admin/documentos — Sprint 13 S13-06 (migrada al portal
 * vecino bajo vista Comision desde apps/frontend-superadmin tras la
 * doctrina Portal vs Superadmin del 2026-05-13).
 *
 * Original: Sprint 12 S12-10 (UI para que la comision publique
 * documentos internos). Ahora vive en el portal bajo el mismo
 * AuthLayout del vecino con secciones extra cuando hay rol comision.
 *
 * Listado + boton "Nuevo documento" que abre un modal con form
 * (titulo + descripcion + url_externa + tipo + vigente_desde +
 * vigente_hasta).
 *
 * RBAC: requireRole='comision' + tipo_cuenta='operativa' (S13-01).
 * Publicar contenido es accion exclusiva de comision por matriz por
 * verbo (S12-17). La cuenta institucional NO accede aqui.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  crearDocumento,
  listDocumentosAdmin,
  retirarDocumento,
} from '../../lib/api/documentos';
import type {
  DocumentoRead,
  TipoDocumento,
} from '../../lib/api/types';
import {
  ETIQUETA_TIPO_DOCUMENTO,
  TIPOS_DOCUMENTO,
} from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { InputFecha } from '../../components/ui/InputFecha';
import { hoy } from '../../lib/tiempo';

function formatFecha(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, anio, mes, dia] = m;
  return `${dia}-${mes}-${anio}`;
}

interface ModalNuevoProps {
  abierto: boolean;
  onCerrar: () => void;
  onCreado: () => void;
}

function ModalNuevo({ abierto, onCerrar, onCreado }: ModalNuevoProps) {
  const fechaHoy = hoy();
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [urlExterna, setUrlExterna] = useState('');
  const [tipo, setTipo] = useState<TipoDocumento>('reglamento');
  const [vigenteDesde, setVigenteDesde] = useState(fechaHoy);
  const [vigenteHasta, setVigenteHasta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!abierto) return null;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await crearDocumento({
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || null,
        url_externa: urlExterna.trim(),
        tipo,
        vigente_desde: vigenteDesde,
        vigente_hasta: vigenteHasta || null,
      });
      onCreado();
      // Reset
      setTitulo('');
      setDescripcion('');
      setUrlExterna('');
      setTipo('reglamento');
      setVigenteDesde(fechaHoy);
      setVigenteHasta('');
    } catch (err) {
      const mensaje =
        err instanceof ApiError
          ? typeof err.body === 'object' && err.body && 'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`
          : 'Error de red';
      setError(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-nuevo-doc-titulo"
    >
      <div
        className="bg-bg-elevated border-2 border-admin-red rounded-md p-6 max-w-xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="modal-nuevo-doc-titulo"
          className="text-xl font-medium text-text-primary mb-4"
        >
          Publicar documento
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="doc-titulo" className="block text-sm font-medium text-text-primary mb-1">
              Título
            </label>
            <input
              id="doc-titulo"
              type="text"
              required
              maxLength={200}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-admin-red focus:ring-1 focus:ring-admin-red/30 outline-none"
            />
          </div>
          <div>
            <label htmlFor="doc-descripcion" className="block text-sm font-medium text-text-primary mb-1">
              Descripción (opcional)
            </label>
            <textarea
              id="doc-descripcion"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={2}
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-admin-red focus:ring-1 focus:ring-admin-red/30 outline-none"
            />
          </div>
          <div>
            <label htmlFor="doc-url" className="block text-sm font-medium text-text-primary mb-1">
              URL del documento (Google Drive u otro)
            </label>
            <input
              id="doc-url"
              type="url"
              required
              maxLength={500}
              value={urlExterna}
              onChange={(e) => setUrlExterna(e.target.value)}
              placeholder="https://drive.google.com/..."
              className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 placeholder:text-text-muted focus:border-admin-red focus:ring-1 focus:ring-admin-red/30 outline-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="doc-tipo" className="block text-sm font-medium text-text-primary mb-1">
                Tipo
              </label>
              <select
                id="doc-tipo"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as TipoDocumento)}
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              >
                {TIPOS_DOCUMENTO.map((t) => (
                  <option key={t} value={t}>
                    {ETIQUETA_TIPO_DOCUMENTO[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="doc-desde" className="block text-sm font-medium text-text-primary mb-1">
                Vigente desde
              </label>
              <InputFecha
                id="doc-desde"
                requerido
                value={vigenteDesde}
                onChange={setVigenteDesde}
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              />
            </div>
            <div>
              <label htmlFor="doc-hasta" className="block text-sm font-medium text-text-primary mb-1">
                Vigente hasta (opcional)
              </label>
              <InputFecha
                id="doc-hasta"
                min={vigenteDesde}
                value={vigenteHasta}
                onChange={setVigenteHasta}
                className="w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2"
              />
            </div>
          </div>

          {error && (
            <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary" role="alert">
              {error}
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button type="button" variant="ghost" onClick={onCerrar}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={enviando}>
              {enviando ? 'Publicando…' : 'Publicar documento'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function DocumentosPage() {
  const [documentos, setDocumentos] = useState<DocumentoRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listDocumentosAdmin({ incluir_no_vigentes: true })
      .then((data) => {
        if (!cancelado) setDocumentos(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar documentos`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey]);

  const hoyISO = hoy();

  async function handleRetirar(doc: DocumentoRead) {
    if (!window.confirm(`¿Retirar "${doc.titulo}" del listado del vecino?`))
      return;
    const ayer = new Date();
    ayer.setDate(ayer.getDate() - 1);
    try {
      await retirarDocumento(doc.id, ayer.toISOString().slice(0, 10));
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const mensaje =
        err instanceof ApiError ? `Error ${err.status}` : 'Error de red';
      window.alert(`No pudimos retirar el documento: ${mensaje}`);
    }
  }

  return (
    <div className="px-8 py-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-admin-red font-medium">
            Super admin · Acceso técnico
          </div>
          <h1 className="text-2xl font-medium text-text-primary mt-1">
            Documentos internos
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Reglamentos, actas e informes del barrio. Los vecinos los ven en el
            portal cuando están vigentes.
          </p>
        </div>
        <Button variant="primary" onClick={() => setModalAbierto(true)}>
          + Nuevo documento
        </Button>
      </div>

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}
      {!loading && !error && documentos.length === 0 && (
        <p className="text-sm text-text-muted">Aún no has publicado documentos.</p>
      )}
      {!loading && !error && documentos.length > 0 && (
        <div className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b-[0.5px] border-white/15 bg-bg-sunken">
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Título
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Tipo
                </th>
                <th className="text-left px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Vigencia
                </th>
                <th className="text-right px-4 py-2 text-xs uppercase tracking-wide text-text-secondary font-medium">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {documentos.map((doc) => {
                const expirado =
                  doc.vigente_hasta !== null && doc.vigente_hasta < hoyISO;
                const futuro = doc.vigente_desde > hoyISO;
                const vigente = !expirado && !futuro;
                return (
                  <tr
                    key={doc.id}
                    className="border-b-[0.5px] border-white/[0.08]"
                    data-testid="fila-documento"
                  >
                    <td className="px-4 py-3">
                      <a
                        href={doc.url_externa}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-text-primary hover:text-orange"
                      >
                        {doc.titulo}
                      </a>
                      {doc.descripcion && (
                        <div className="text-xs text-text-muted mt-1">{doc.descripcion}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary">
                      {ETIQUETA_TIPO_DOCUMENTO[doc.tipo] ?? doc.tipo}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {vigente && (
                        <span className="inline-flex items-center px-2 py-[2px] bg-state-success/20 text-state-success rounded-xs text-xs font-medium">
                          Vigente
                        </span>
                      )}
                      {expirado && (
                        <span className="inline-flex items-center px-2 py-[2px] bg-bg-sunken text-text-muted rounded-xs text-xs">
                          Expirado
                        </span>
                      )}
                      {futuro && (
                        <span className="inline-flex items-center px-2 py-[2px] bg-state-info/20 text-state-info rounded-xs text-xs">
                          Futuro
                        </span>
                      )}
                      <div className="text-xs text-text-muted mt-1">
                        Desde {formatFecha(doc.vigente_desde)}
                        {doc.vigente_hasta && ` · hasta ${formatFecha(doc.vigente_hasta)}`}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {vigente && (
                        <button
                          type="button"
                          onClick={() => handleRetirar(doc)}
                          className="text-sm text-state-danger hover:underline"
                        >
                          Retirar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ModalNuevo
        abierto={modalAbierto}
        onCerrar={() => setModalAbierto(false)}
        onCreado={() => {
          setModalAbierto(false);
          setRefreshKey((k) => k + 1);
        }}
      />
    </div>
  );
}
